<template>
  <div class="nurse" v-if="nurse">
    <breadcrumbs :items="breadcrumbs">
      <div class="activation">
        <div class="nurse-action">
          <rp-status :statusInfo="nurse.activated_at" />
        </div>

        <div class="nurse-action">
          <rp-permissions :requireAll="['write_users']">

            <rp-button
              border-radius
              button-type="normal"
              @click="activateNurse">
              {{nurseActive === 'Active' ? 'deactivate' : 'activate'}}
            </rp-button>
          </rp-permissions>
          
        </div>

        <div class="nurse-action">
          <rp-permissions :requireAll="['write_users']">
            <router-link :to="{ name: 'edit-nurse', params: { id: this.$route.params.id}}">
              <rp-sprite class="create-icon" name="create" />
            </router-link>
            <rp-sprite @click.native="activeConfirm = !activeConfirm" class="create-icon" name="delete_outline" />
          </rp-permissions>

        </div>
      </div>
    </breadcrumbs>

    <div class="content">
      <div class="nurse-info">
        <div class="info-group">
          <h3>Status</h3>
          <p>{{nurseActive}}</p>
        </div>
        <div class="info-group">
          <h3>First Name</h3>
          <p>{{nurse.profile.first_name ? nurse.profile.first_name : 'NOT PROVIDED'}}</p>
        </div>
        <div class="info-group">
          <h3>Last Name</h3>
          <p>{{nurse.profile.last_name ? nurse.profile.last_name : 'NOT PROVIDED'}}</p>
        </div>
        <div class="info-group">
          <h3>Email Address</h3>
          <p>{{nurse.email ? nurse.email : 'NOT PROVIDED'}}</p>
        </div>
        <div class="info-group">
          <h3>Gender</h3>
          <p>{{nurse.profile.gender | genderFilter}}</p>
        </div>
        <div class="table-container info-group" v-if="showData && nurse.surgeons.length > 0">
          
          <h3>Surgeons:</h3>
          <rp-table
            name="users"
            noPagination
            :results="getSurgeons"
            :key="update"
            :headings="['name', 'email', 'gender', {heading: 'disassociate', key: 'disassociate', button: true}]"
            @rowClick="onSurgeonClick">
              <div
                v-for="(surgeon, index) in nurse['surgeons']"
                :key="index"
                :slot="'td-' + index + '-disassociate'">
                <span
                  @click.stop="activeUnlinkDelete(surgeon)">
                    <rp-sprite class="create-icon" name="disassociate"/>
                </span>
              </div>
          </rp-table>

        </div>
      </div>

      <rp-overlay :active="activeOverlay">
        <rp-alert
          icon="done"
          :message="successMessage"
          :onClick="successFunction" />
      </rp-overlay>

      <rp-overlay :active="activeConfirm">
        <rp-delete-confirm
          icon="warning"
          userType="Nurse"
          messageType='Delete '
          :userName="userName"
          :cancel="cancelDeletion"
          :confirm="confirmDeletion" />
      </rp-overlay>

      <rp-overlay :active="activeDelete">
        <rp-alert
          icon="done"
          :message="alertMessage"
          :onClick="alertFunction" />
      </rp-overlay>
      
      <rp-overlay :active="activeUnlink">
        <rp-delete-confirm
          icon="warning"
          :messageText="disassociateConfirmText"
          :cancel="closeModal"
          :confirm="disassociateConfirmFunction"/>
      </rp-overlay>
    </div>
  </div>
</template>

<script>
import Breadcrumbs from '@/components/Breadcrumbs'
import Status from 'raypro_ui/src/components/ui/Status'
import Button from 'raypro_ui/src/components/ui/inputs/Button'
import Sprite from 'raypro_ui/src/components/ui/Sprite'
import Overlay from 'raypro_ui/src/components/ui/Overlay'
import DeleteConfirm from 'raypro_ui/src/components/ui/DeleteConfirm'
import Alert from 'raypro_ui/src/components/ui/Alert'
import Table from 'raypro_ui/src/components/ui/Table'
import page from '@/mixins/page'
import Permissions from 'raypro_ui/src/components/Permissions'

// API
import {getUserDetails, activateUser, deactivateUser, deleteUser, removeNurseFromSurgeon} from 'raypro_ui/src/api/users'

export default {
  components: {
    Breadcrumbs,
    'rp-status': Status,
    'rp-button': Button,
    'rp-sprite': Sprite,
    'rp-table': Table,
    'rp-overlay': Overlay,
    'rp-alert': Alert,
    'rp-delete-confirm': DeleteConfirm,
    'rp-permissions': Permissions
  },

  data() {
    return {
      operations: [],
      nurse: null,
      activeOverlay: false,
      activeConfirm: false,
      activeDelete: false,
      title: 'Nurse',
      showData: false,
      update: 0,
      page: {
        totalPages: 1,
        totalentries: 0,
        pageSize: 10,
        pageNumber: 1
      },
      activeUnlink: false,
      disassociateConfirmText: '',
      disassociateConfirmFunction: null,
      disassociateObject: null
    }
  },

  props: {id: String},

  watch: {
    id: function() {
      this.getUser()
    }
  },

  mixins: [page],

  mounted() {
    this.getUser()
  },

  filters: {
    genderFilter(gender) {
      if(gender === 'NURSE DELETED') {
        return gender
      }
      if(gender === 'M') {
        return 'Male'
      }
      if(gender === 'F') {
        return 'Female'
      }
      if(gender === 'O') {
        return 'Prefer not to say'
      }

      return 'NOT PROVIDED'
    }
  },

  computed: {
    userName() {
      return `${this.nurse.profile.first_name} ${this.nurse.profile.last_name}`
    },

    alertMessage() {
      return `Nurse ${this.nurse.profile.first_name} ${this.nurse.profile.last_name} deleted successfully!`
    },

    salutation() {
      return this.nurse.profile.salutation || ''
    },

    currentBreadcrumb() {
      return `${this.salutation} ${this.userName}`
    },

    breadcrumbs() {
      return [
        {
          label: 'Nurses',
          link: 'all-users',
          params: {role: 'nurse'}
        },
        {label: this.currentBreadcrumb}
      ]
    },

    nurseActive() {
      return this.nurse.activated_at ? 'Active' : 'Inactive'
    },

    successMessage() {
      return `Nurse ${this.nurseActive === 'Active' ? 'deactivated' : 'activated'} successfully!`
    }
  },

  methods: {
    activeUnlinkDelete(obj) {
      this.disassociateConfirmText = `Unassociate nurse ${obj.profile.first_name} ${obj.profile.last_name}`
      this.disassociateObject = obj
      this.disassociateConfirmFunction = this.disassociateSurgeon
      this.activeUnlink = !this.activeUnlink
    },

    closeModal() {
      this.activeUnlink = !this.activeUnlink
    },

    disassociateSurgeon() {
      const payload = {
        userId: this.disassociateObject.id,
        nurseId: this.nurse.id
      }

      removeNurseFromSurgeon(payload).then(() => {
        this.getUser()
        this.closeModal()
      })
    },

    getUser() {
      getUserDetails(this.id).then(({data}) => {
        this.nurse = data
        this.showData = true
        this.update++
      })
    },

    getSurgeons() {
      return new Promise((resolve) => {
        const mapped = this.nurse.surgeons.map((row) => {
          return {
            id: row.id,
            name: `${row.profile.first_name} ${row.profile.last_name}`,
            email: row.email,
            // TODO: map to age group
            dob: row.profile.dob,
            gender: row.profile.gender
          }
        })

        resolve({
          data: mapped,
          page
        })
      })
    },

    onSurgeonClick(row) {
      this.$router.push(`/surgeons/${row.id}`)
    },

    successFunction() {
      this.activeOverlay = false
      this.getUser()
    },

    activateNurse() {
      if(this.nurseActive === 'Active') {
        deactivateUser({id: this.id})
          .then(() => {
            this.activeOverlay = true
          })
          .catch(({response}) => {
            this.errors = response.errors
          })
      } else {
        activateUser({id: this.id})
          .then(() => {
            this.activeOverlay = true
          })
          .catch(({response}) => {
            this.errors = response.errors
          })
      }
    },

    alertFunction() {
      this.$router.push({name: 'all-users'})
    },

    cancelDeletion() {
      this.activeConfirm = !this.activeConfirm
    },

    confirmDeletion() {
      deleteUser({id: this.id})
        .then(() => {
          this.activeConfirm = !this.activeConfirm
          this.activeDelete = !this.activeDelete
        })
    }
  }
}
</script>

<style lang="sass">
@import 'raypro_ui/src/styles/base/_variables.sass'
@import 'raypro_ui/src/styles/helpers/_mixins.sass'

.activation
  display: flex
  align-items: center
  justify-content: flex-end
  .nurse-action
    margin-left: 15px
    .btn
      margin-top: 0px
      color: $color-text-invert
      border-color: $color-grey

.nurse
  .content
    display: flex
    flex-direction: column
    @include media($media-medium)
      padding: 0 40px
      flex-direction: row
      justify-content: space-between
    .nurse-info
      width: 100%
      .info-group
        display: flex
        flex-direction: column
        @include media($media-medium)
          flex-direction: row
          align-items: center
          width: 40%
        h3
          display: inline-flex
          width: 150px
          min-width: 150px
          margin: 0
          text-transform: initial
          font-weight: lighter
        p
          display: inline-flex
          line-height: 1
          font-weight: bold
          word-break: break-word
      .table-container
        flex-direction: column
        align-items: flex-start
        width: 100%
        h3
          margin: 15px 0
        .table
          margin-bottom: 15px
          width: 100%
      hr
        margin: 20px 0 36px
        border-top: 1px solid $color-grey-dark
    .modal-header
      padding: 20px
      font-size: 20px
      border-bottom: 2px solid #e2e4e4

    .modal-body
      min-height: 500px

</style>



// WEBPACK FOOTER //
// src/components/pages/Nurses/Nurse.vue